<template>
  <div class="car-map-container">
    <div id="carMap" ref="mapContainer" class="map-container"></div>
    <div class="map-legend">
      <div class="legend-section">
        <div class="legend-title">车辆状态</div>
        <div class="legend-item">
          <span class="legend-icon" style="background-color: #52c41a;"></span>
          <span>空闲</span>
        </div>
        <div class="legend-item">
          <span class="legend-icon" style="background-color: #1890ff;"></span>
          <span>使用中</span>
        </div>
        <div class="legend-item">
          <span class="legend-icon" style="background-color: #ff4d4f;"></span>
          <span>故障</span>
        </div>
        <div class="legend-item">
          <span class="legend-icon" style="background-color: #faad14;"></span>
          <span>维护中</span>
        </div>
        <div class="legend-item">
          <span class="legend-icon" style="background-color: #999;"></span>
          <span>离线</span>
        </div>
      </div>
      <div class="legend-section" v-if="showAreaBoundaries && selectedAreaId">
        <div class="legend-title">区域边界</div>
        <div class="legend-item">
          <span class="legend-icon" style="border: 2px solid #1890ff; background-color: rgba(24, 144, 255, 0.1);"></span>
          <span>选中区域</span>
        </div>
      </div>
    </div>
    <div class="map-controls">
      <Button size="small" @click="fitBounds">适应所有车辆</Button>
      <Button size="small" @click="centerMap">回到中心</Button>
      <Button size="small" @click="refreshMarkers">刷新标记</Button>
      <Button 
        size="small" 
        :type="showAreaBoundaries ? 'primary' : 'default'"
        @click="toggleAreaBoundaries"
      >
        {{ showAreaBoundaries ? '隐藏区域' : '显示区域' }}
      </Button>
    </div>
  </div>
</template>

<script>
const DEFAULT_ICON_SIZE = 48 // 减小尺寸，更精致专业
const ICON_TIP_OFFSET = 6 // 图标底部尖角距离总高度的偏移

export default {
  name: 'CarMap',
  props: {
    cars: {
      type: Array,
      default: () => []
    },
    center: {
      type: Array,
      default: () => [116.397428, 39.90923] // 默认北京
    },
    zoom: {
      type: Number,
      default: 13
    },
    areaBoundaries: {
      type: Array,
      default: () => []
    },
    selectedAreaId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      markerData: [], // 保存标记对应的车辆数据，用于缩放时重新创建图标
      markerPositions: [], // 保存标记的原始坐标，避免从LngLat对象获取时出错
      infoWindows: [],
      areaPolygons: [], // 区域边界多边形
      areaInfoWindows: [], // 区域信息窗口
      centerMarker: null, // 区域中心点标记
      mapLoaded: false,
      showAreaBoundaries: true, // 默认显示区域边界
      hasFittedToArea: false, // 标记是否已经适应过区域边界
      currentZoom: 13, // 当前缩放级别
      zoomUpdateTimer: null // 缩放更新防抖定时器
    }
  },
  watch: {
    cars: {
      handler(newCars) {
        if (this.mapLoaded && this.map) {
          this.updateMarkers(newCars)
        }
      },
      deep: true
    },
    areaBoundaries: {
      handler(newBoundaries) {
        if (this.mapLoaded && this.map) {
          this.updateAreaBoundaries(newBoundaries)
        }
      },
      deep: true
    },
    selectedAreaId: {
      handler(newAreaId, oldAreaId) {
        if (this.mapLoaded && this.map) {
          this.highlightArea(newAreaId)
          // 如果区域ID变化，重置适应标记，允许重新适应新区域
          if (newAreaId !== oldAreaId) {
            this.hasFittedToArea = false
            // 如果有新区域边界，自动适应
            if (newAreaId && this.areaBoundaries && this.areaBoundaries.length > 0) {
              this.$nextTick(() => {
                this.fitToSelectedArea()
              })
            }
          }
        }
      }
    },
    showAreaBoundaries: {
      handler(show) {
        if (this.mapLoaded && this.map) {
          if (show) {
            this.updateAreaBoundaries(this.areaBoundaries)
          } else {
            this.clearAreaBoundaries()
          }
        }
      }
    }
  },
  mounted() {
    this.loadAMapScript()
  },
  methods: {
    // 加载高德地图脚本
    loadAMapScript() {
      // 检查是否已经加载
      if (window.AMap) {
        this.initMap()
        return
      }

      // 等待一段时间，如果HTML中已经引入了高德地图，直接初始化
      setTimeout(() => {
        if (window.AMap) {
          this.initMap()
        } else {
          // 如果HTML中没有引入，动态加载
          const script = document.createElement('script')
          script.type = 'text/javascript'
          script.async = true
          script.defer = true
          // 使用高德地图Web服务API Key（需要替换为你的Key）
          // 如果没有Key，可以使用公开的测试Key，但有限制
          script.src = 'https://webapi.amap.com/maps?v=1.4.15&key=YOUR_AMAP_KEY&callback=initAMap'
          
          // 设置全局回调
          window.initAMap = () => {
            this.initMap()
          }

          // 错误处理
          script.onerror = () => {
            console.error('高德地图加载失败，请检查网络连接或API Key')
            this.$Message.error('地图加载失败，请检查网络连接或API Key配置')
          }

          document.head.appendChild(script)
        }
      }, 100)
    },

    // 初始化地图
    initMap() {
      if (!window.AMap) {
        console.error('高德地图API未加载')
        return
      }

      try {
        this.map = new window.AMap.Map('carMap', {
          zoom: this.zoom,
          center: this.center,
          mapStyle: 'amap://styles/normal', // 地图样式
          viewMode: '3D', // 3D视图
          pitch: 0,
          rotation: 0
        })

        // 添加地图控件
        this.map.addControl(new window.AMap.Scale()) // 比例尺
        this.map.addControl(new window.AMap.ToolBar({
          position: 'RT' // 右上角
        })) // 工具条

        this.mapLoaded = true
        this.currentZoom = this.map.getZoom() || this.zoom

        // 地图加载完成后，更新标记和区域边界
        this.map.on('complete', () => {
          this.updateMarkers(this.cars)
          if (this.showAreaBoundaries) {
            this.updateAreaBoundaries(this.areaBoundaries)
          }
        })

        // 监听地图缩放事件，动态调整图标大小（SVG 矢量图优势）
        this.map.on('zoomchange', () => {
          this.updateIconSizes()
        })
      } catch (error) {
        console.error('地图初始化失败:', error)
        this.$Message.error('地图初始化失败')
      }
    },

    // 更新地图标记
    updateMarkers(cars) {
      if (!this.map) {
        console.warn('地图未初始化，无法更新标记')
        return
      }

      console.log('updateMarkers: 收到车辆数据', cars.length, '辆')
      console.log('updateMarkers: 清除前标记数量', this.markers.length, 'markerData数量', this.markerData.length)
      
      // 清除旧标记
      this.clearMarkers()
      
      console.log('updateMarkers: 清除后标记数量', this.markers.length, 'markerData数量', this.markerData.length)

      // 过滤有位置的车辆（支持多种location格式）
      const carsWithLocation = cars.filter(car => {
        if (!car.location) {
          console.log('车辆无位置信息:', car.car_name || car.car_ref)
          return false
        }
        
        // 支持GeoJSON格式：{ type: "Point", coordinates: [lng, lat] }
        if (car.location.coordinates && Array.isArray(car.location.coordinates) && car.location.coordinates.length === 2) {
          console.log('车辆位置格式: GeoJSON', car.car_name || car.car_ref, car.location.coordinates)
          return true
        }
        
        // 支持数组格式：[lng, lat]
        if (Array.isArray(car.location) && car.location.length === 2) {
          console.log('车辆位置格式: 数组', car.car_name || car.car_ref, car.location)
          return true
        }
        
        console.warn('车辆位置格式不支持:', car.car_name || car.car_ref, car.location)
        return false
      })

      console.log('updateMarkers: 有位置的车辆', carsWithLocation.length, '辆')
      console.log('updateMarkers: 地图对象:', !!this.map, '地图是否已加载:', this.mapLoaded)
      console.log('updateMarkers: 当前标记数量:', this.markers.length)

      if (carsWithLocation.length === 0) {
        console.warn('没有车辆有位置信息，无法在地图上显示')
        return
      }
      
      if (!this.map) {
        console.error('updateMarkers: 地图对象不存在，无法添加标记')
        return
      }
      
      if (!this.mapLoaded) {
        console.warn('updateMarkers: 地图尚未完全加载，等待地图加载完成')
        return
      }

      // 创建标记（统一处理GeoJSON格式）
      carsWithLocation.forEach(car => {
        // 统一处理：后端现在统一返回GeoJSON格式 { type: "Point", coordinates: [lng, lat] }
        let longitude, latitude
        
        if (!car.location) {
          console.warn('车辆无位置信息:', car.car_name || car.car_ref)
          return
        }
        
        // 统一处理GeoJSON格式
        if (car.location.coordinates && Array.isArray(car.location.coordinates)) {
          // 使用slice()创建新数组，避免Vue响应式对象的影响
          const coordsArray = car.location.coordinates.slice(0, 2)
          longitude = Number(coordsArray[0])
          latitude = Number(coordsArray[1])
        } else {
          console.warn('车辆位置格式不正确，期望GeoJSON格式:', car.car_name || car.car_ref, car.location)
          return
        }
        
        // 验证坐标有效性
        if (isNaN(longitude) || isNaN(latitude)) {
          console.warn('车辆坐标无效:', car.car_name || car.car_ref, { longitude, latitude })
          return
        }
        
        // 验证坐标范围（经度：-180到180，纬度：-90到90）
        if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) {
          console.warn('车辆坐标超出范围:', car.car_name || car.car_ref, { longitude, latitude })
          return
        }
        
        // 检测并处理重叠标记（避免多个车辆在同一位置时标记重叠）
        const overlapThreshold = 0.00001 // 约1米的距离阈值（经纬度）
        let adjustedLongitude = longitude
        let adjustedLatitude = latitude
        let overlapOffset = null
        
        // 检查是否与已创建的标记位置重叠
        for (const existingPos of this.markerPositions) {
          const distanceLng = Math.abs(existingPos[0] - longitude)
          const distanceLat = Math.abs(existingPos[1] - latitude)
          
          // 如果距离小于阈值，认为是重叠
          if (distanceLng < overlapThreshold && distanceLat < overlapThreshold) {
            // 计算重叠标记的数量（包括已存在的标记和当前标记）
            // 注意：当前标记还未添加到 markerPositions，所以需要 +1
            const existingOverlapCount = this.markerPositions.filter(pos => {
              const distLng = Math.abs(pos[0] - longitude)
              const distLat = Math.abs(pos[1] - latitude)
              return distLng < overlapThreshold && distLat < overlapThreshold
            }).length
            const overlapCount = existingOverlapCount + 1 // +1 表示包括当前标记
            
            // 为重叠标记添加螺旋式偏移，确保所有标记都可见
            // 偏移角度：每个标记旋转一定角度（从0度开始，每个标记增加60度）
            const angle = (overlapCount * 60) * (Math.PI / 180) // 60度间隔
            const offsetDistance = overlapThreshold * 2 // 偏移距离约2米
            adjustedLongitude = longitude + Math.cos(angle) * offsetDistance
            adjustedLatitude = latitude + Math.sin(angle) * offsetDistance
            overlapOffset = {
              original: [longitude, latitude],
              adjusted: [adjustedLongitude, adjustedLatitude],
              overlapCount: overlapCount,
              existingOverlapCount: existingOverlapCount
            }
            
            console.log('检测到标记重叠，添加偏移:', {
              carName: car.car_name || car.car_ref,
              originalPosition: [longitude, latitude],
              adjustedPosition: [adjustedLongitude, adjustedLatitude],
              existingOverlapCount: existingOverlapCount,
              totalOverlapCount: overlapCount,
              offsetDistance: offsetDistance,
              offsetAngle: (overlapCount * 60) + '度'
            })
            break
          }
        }
        
        const position = [adjustedLongitude, adjustedLatitude]
        
        // 重新计算车辆是否在区域内（根据实际坐标和区域边界）
        const isInArea = this.checkCarInArea(car)
        
        console.log('创建车辆标记:', car.car_name || car.car_ref, {
          position: position,
          originalPosition: overlapOffset ? overlapOffset.original : null,
          area_id: car.area_id,
          backend_in_area: car.in_area,
          calculated_in_area: isInArea,
          area_name: car.area_name
        })

        // 创建自定义图标（使用 SVG 矢量图）
        // 根据当前缩放级别计算初始图标大小
        const currentZoom = this.map ? this.map.getZoom() : this.zoom
        const initialIconSize = this.calculateIconSizeByZoom(currentZoom)
        const icon = this.createCarIcon(car, isInArea, initialIconSize)

        // 创建标记（zIndex在创建时设置，高德地图Marker不支持setZIndex方法）
        const markerZIndex = this.getMarkerZIndex(car)
        const marker = new window.AMap.Marker({
          position: position,
          icon: icon,
          title: `${car.car_name || car.car_ref || '车辆'}`,
          // offset 设置为 (0, 0)，因为图标的 anchor 已经处理了定位（尖角底部对齐坐标点）
          offset: new window.AMap.Pixel(0, 0),
          zIndex: markerZIndex // 在创建时设置zIndex
        })

        // 创建信息窗口内容（传递重新计算的 in_area 值）
        const infoWindow = new window.AMap.InfoWindow({
          content: this.createInfoWindowContent(car, isInArea),
          offset: new window.AMap.Pixel(0, -30),
          closeWhenClickMap: true
        })

        // 方案A：专业地图应用做法
        // 不显示文字标签，只通过图标颜色区分状态
        // 点击图标会显示详细信息窗口，保持地图简洁清晰
        // 状态通过图标颜色识别：绿色(空闲)、蓝色(使用中)、红色(故障/越界)、黄色(维护中)、灰色(离线)

        // 点击标记显示信息窗口
        marker.on('click', () => {
          // 关闭其他信息窗口
          this.infoWindows.forEach(iw => iw.close())
          
          // 从 markerData 中查找当前标记对应的数据，获取原始坐标
          const currentMarkerData = this.markerData.find(md => md.marker === marker)
          if (!currentMarkerData) {
            // 如果找不到数据，直接显示当前标记的信息窗口
            infoWindow.open(this.map, marker.getPosition())
            setTimeout(() => {
              const btn = document.querySelector(`.car-detail-btn[data-car-id="${car._id}"]`)
              if (btn) {
                btn.onclick = (e) => {
                  e.stopPropagation()
                  this.$emit('marker-click', car)
                }
              }
            }, 100)
            return
          }
          
          // 检测是否有重叠标记（基于原始坐标）
          const originalPos = currentMarkerData.originalPosition || currentMarkerData.position
          const overlapThreshold = 0.00001 // 约1米的距离阈值
          const overlappingCars = this.markerData.filter(markerData => {
            // 使用原始坐标进行比较
            const pos = markerData.originalPosition || markerData.position
            const distLng = Math.abs(pos[0] - originalPos[0])
            const distLat = Math.abs(pos[1] - originalPos[1])
            return distLng < overlapThreshold && distLat < overlapThreshold
          })
          
          // 如果有多个重叠标记，显示列表窗口
          if (overlappingCars.length > 1) {
            const overlapInfoWindow = new window.AMap.InfoWindow({
              content: this.createOverlapInfoWindowContent(overlappingCars),
              offset: new window.AMap.Pixel(0, -30),
              closeWhenClickMap: true
            })
            overlapInfoWindow.open(this.map, marker.getPosition())
            
            // 绑定列表中的点击事件（整个item都可以点击）
            setTimeout(() => {
              overlappingCars.forEach((markerData, index) => {
                const item = document.querySelector(`.overlap-car-item[data-car-index="${index}"]`)
                if (item) {
                  item.onclick = (e) => {
                    e.stopPropagation()
                    // 关闭列表窗口
                    overlapInfoWindow.close()
                    // 显示选中车辆的详细信息窗口
                    const selectedCar = markerData.car
                    const selectedIsInArea = markerData.isInArea
                    const selectedInfoWindow = new window.AMap.InfoWindow({
                      content: this.createInfoWindowContent(selectedCar, selectedIsInArea),
                      offset: new window.AMap.Pixel(0, -30),
                      closeWhenClickMap: true
                    })
                    selectedInfoWindow.open(this.map, marker.getPosition())
                    
                    // 绑定详情窗口中的按钮点击事件
                    setTimeout(() => {
                      const detailBtn = document.querySelector(`.car-detail-btn[data-car-id="${selectedCar._id}"]`)
                      if (detailBtn) {
                        detailBtn.onclick = (e) => {
                          e.stopPropagation()
                          this.$emit('marker-click', selectedCar)
                        }
                      }
                    }, 100)
                  }
                }
              })
            }, 100)
          } else {
            // 只有一个标记，直接显示信息窗口
            infoWindow.open(this.map, marker.getPosition())
            
            // 绑定信息窗口中的按钮点击事件
            setTimeout(() => {
              const btn = document.querySelector(`.car-detail-btn[data-car-id="${car._id}"]`)
              if (btn) {
                btn.onclick = (e) => {
                  e.stopPropagation()
                  this.$emit('marker-click', car)
                }
              }
            }, 100)
          }
        })

        // 添加到地图
        try {
          // 使用 map.add() 方法添加标记，避免可能的 setZIndex 问题
          this.map.add(marker)
          this.markers.push(marker)
          
          console.log('标记添加详情:', {
            carName: car.car_name || car.car_ref,
            markerAdded: true,
            markersCount: this.markers.length,
            markerDataCount: this.markerData.length + 1, // +1 因为还没push
            position: [longitude, latitude]
          })
          // 保存标记对应的车辆数据，用于缩放时重新创建图标
          this.markerData.push({
            marker: marker,
            car: car,
            isInArea: isInArea,
            position: [Number(adjustedLongitude), Number(adjustedLatitude)],
            originalPosition: overlapOffset ? overlapOffset.original : [Number(longitude), Number(latitude)], // 保存原始坐标用于重叠检测和显示
            infoWindow: infoWindow
          })
          // 保存调整后的坐标（用于重叠检测），避免Vue响应式对象的影响
          this.markerPositions.push([Number(adjustedLongitude), Number(adjustedLatitude)])
          this.infoWindows.push(infoWindow)
          console.log('车辆标记已添加到地图:', car.car_name || car.car_ref, {
            position: position,
            longitude: longitude,
            latitude: latitude,
            zIndex: markerZIndex,
            markerOnMap: marker.getMap() === this.map,
            markerPosition: marker.getPosition ? marker.getPosition().toString() : 'N/A',
            totalMarkers: this.markers.length,
            markerDataCount: this.markerData.length
          })
          
          // 验证标记是否真的在地图上
          setTimeout(() => {
            try {
              // 检查地图对象是否存在且已初始化
              if (!this.map) {
                console.warn('车辆标记验证: 地图对象不存在', car.car_name || car.car_ref)
                return
              }
              
              const markerPosition = marker.getPosition()
              const isOnMap = marker.getMap() === this.map
              const icon = marker.getIcon()
              
              // 安全地获取地图边界（避免地图未完全初始化时出错）
              let mapBounds = null
              let isInView = false
              try {
                if (this.map && typeof this.map.getBounds === 'function') {
                  mapBounds = this.map.getBounds()
                  if (mapBounds && markerPosition && typeof mapBounds.contains === 'function') {
                    isInView = mapBounds.contains(markerPosition)
                  }
                }
              } catch (boundsError) {
                // 地图边界获取失败，可能是地图尚未完全初始化，但不影响标记显示
                console.debug('车辆标记验证: 地图边界获取失败（地图可能尚未完全初始化）', car.car_name || car.car_ref, boundsError.message)
              }
              
              // 安全地获取图标大小
              let iconSize = 'N/A'
              if (icon) {
                try {
                  if (typeof icon.getSize === 'function') {
                    const size = icon.getSize()
                    iconSize = size ? { width: size.getWidth(), height: size.getHeight() } : 'N/A'
                  } else if (icon.size) {
                    iconSize = icon.size
                  }
                } catch (e) {
                  // 忽略获取图标大小的错误
                }
              }
              
              // 安全地获取地图中心点
              let mapCenter = 'N/A'
              try {
                const center = this.map.getCenter()
                if (center && typeof center.getLng === 'function' && typeof center.getLat === 'function') {
                  mapCenter = {
                    lng: center.getLng(),
                    lat: center.getLat()
                  }
                }
              } catch (e) {
                // 忽略获取地图中心的错误
              }
              
              console.log('车辆标记验证:', {
                carName: car.car_name || car.car_ref,
                isOnMap: isOnMap,
                markerPosition: markerPosition ? { 
                  lng: markerPosition.getLng(), 
                  lat: markerPosition.getLat() 
                } : null,
                iconSize: iconSize,
                zIndex: markerZIndex,
                isInView: isInView,
                mapCenter: mapCenter,
                mapZoom: this.map && typeof this.map.getZoom === 'function' ? this.map.getZoom() : 'N/A'
              })
              
              // 如果标记不在视野内，提示用户（但不影响标记显示）
              if (isInView === false && mapBounds !== null) {
                console.warn('⚠️ 车辆标记不在当前地图视野内，可能需要调整地图视野:', car.car_name || car.car_ref)
              }
            } catch (err) {
              // 验证失败不影响标记显示，只记录警告
              console.warn('车辆标记验证失败（不影响标记显示）:', car.car_name || car.car_ref, err.message)
            }
          }, 500)
        } catch (error) {
          console.error('添加车辆标记失败:', error, car.car_name || car.car_ref, position)
        }
      })

      // 如果有车辆，自动适应视野
      if (this.markers.length > 0) {
        // 延迟执行，确保标记已完全添加到地图
        this.$nextTick(() => {
          this.fitBounds()
        })
      }
    },

    // 创建车辆图标（使用 SVG 矢量图，可随地图缩放保持清晰）
    createCarIcon(car, isInArea = true, iconSize = null) {
      const status = car.status || '空闲'
      const online = car.online !== false
      
      // 如果没有指定尺寸，使用默认尺寸
      if (!iconSize) {
        iconSize = DEFAULT_ICON_SIZE
      }

      // 根据状态和是否在区域内选择对应的 SVG 文件
      // SVG 文件路径（放在 public 目录下）
      const svgName = this.getStatusSvgName(status, online, isInArea)
      const svgPath = `/location_${svgName}.svg`

      console.log('创建图标:', {
        carName: car.car_name || car.car_ref,
        status: status,
        online: online,
        isInArea: isInArea,
        svgName: svgName,
        svgPath: svgPath,
        iconSize: iconSize
      })

      // 计算锚点：假设 SVG 的尖角底部在图标底部中心
      // 如果 SVG 的锚点不在底部，需要根据实际 SVG 尺寸调整
      const anchorX = iconSize / 2
      const anchorY = iconSize
      
      const icon = new window.AMap.Icon({
        image: svgPath,
        size: new window.AMap.Size(iconSize, iconSize),
        imageSize: new window.AMap.Size(iconSize, iconSize),
        // 使用 anchor 属性设置锚点（相对于图标左上角的像素偏移）
        anchor: new window.AMap.Pixel(anchorX, anchorY)
      })

      // 添加图标加载错误处理
      const img = new Image()
      img.onerror = () => {
        console.error('SVG图标加载失败:', svgPath, '车辆:', car.car_name || car.car_ref)
      }
      img.onload = () => {
        console.log('SVG图标加载成功:', svgPath, '车辆:', car.car_name || car.car_ref)
      }
      img.src = svgPath

      return icon
    },

    // 获取状态对应的 SVG 文件名
    getStatusSvgName(status, online, isInArea) {
      // 如果越界，强制使用红色
      if (!isInArea) {
        return 'red'
      }
      
      // 如果离线，使用灰色
      if (!online) {
        return 'gray'
      }
      
      // 根据状态返回对应的颜色
      switch (status) {
        case '空闲':
          return 'green'
        case '使用中':
        case '前往':
        case '到达':
        case '等待':
        case '开仓':
        case '关仓':
          return 'blue'
        case '故障':
          return 'red'
        case '维护中':
          return 'yellow'
        default:
          return 'green'
      }
    },

    // 创建信息窗口内容
    // 创建重叠标记的信息窗口内容（显示多个车辆列表）
    createOverlapInfoWindowContent(overlappingCars) {
      const carsList = overlappingCars.map((markerData, index) => {
        const car = markerData.car
        const status = car.status || '空闲'
        const online = car.online !== false
        const isInArea = markerData.isInArea
        
        // 从 areaBoundaries 中查找区域名称（如果车辆数据中没有 area_name）
        let areaName = car.area_name
        if (!areaName && car.area_id && this.areaBoundaries && this.areaBoundaries.length > 0) {
          const vehicleArea = this.areaBoundaries.find(a => 
            Number(a.area_id) === Number(car.area_id)
          )
          if (vehicleArea && vehicleArea.area_name) {
            areaName = vehicleArea.area_name
          }
        }
        areaName = areaName || '未知区域'
        
        return `
          <div class="overlap-car-item" 
               data-car-index="${index}"
               style="padding: 10px; margin-bottom: 8px; border: 1px solid #e8e8e8; border-radius: 6px; cursor: pointer; transition: all 0.2s; background: #fff;"
               onmouseover="this.style.background='#f5f5f5'; this.style.borderColor='#1890ff';"
               onmouseout="this.style.background='#fff'; this.style.borderColor='#e8e8e8';">
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <div style="width: 10px; height: 10px; border-radius: 50%; background-color: ${this.getStatusColor(status, online)}; margin-right: 8px;"></div>
              <div style="font-weight: 600; font-size: 15px; color: #333; flex: 1;">
                ${car.car_name || car.car_ref || '车辆'}
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 13px; color: #666;">
              <span>编号: ${car.car_ref || '未知'}</span>
              <span style="color: ${this.getStatusColor(status, online)}; font-weight: 500;">
                ${status} ${online ? '●' : '○'}
              </span>
            </div>
            <div style="margin-top: 4px; font-size: 12px; color: #999;">
              区域状态: <span style="color: ${isInArea ? '#52c41a' : '#ff4d4f'};">
                ${isInArea ? '在区域内' : '越界'}
              </span>
            </div>
          </div>
        `
      }).join('')
      
      return `
        <div style="padding: 12px; min-width: 240px; max-width: 320px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <div style="margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #1890ff;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #1890ff;">
              该位置有 ${overlappingCars.length} 辆车
            </h3>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              点击下方车辆查看详细信息
            </div>
          </div>
          <div style="max-height: 400px; overflow-y: auto;">
            ${carsList}
          </div>
        </div>
      `
    },

    createInfoWindowContent(car, calculatedInArea = null) {
      const status = car.status || '空闲'
      const online = car.online !== false
      const battery = car.battery !== null && car.battery !== undefined ? car.battery : '未知'
      
      // 从 areaBoundaries 中查找区域名称（如果车辆数据中没有 area_name）
      let areaName = car.area_name
      if (!areaName && car.area_id && this.areaBoundaries && this.areaBoundaries.length > 0) {
        const vehicleArea = this.areaBoundaries.find(a => 
          Number(a.area_id) === Number(car.area_id)
        )
        if (vehicleArea && vehicleArea.area_name) {
          areaName = vehicleArea.area_name
        }
      }
      areaName = areaName || '未知区域'
      
      const location = car.location_address || '未知位置'
      
      // 使用重新计算的结果，如果没有则使用后端返回的值
      const isInArea = calculatedInArea !== null ? calculatedInArea : (car.in_area !== false)

      return `
        <div style="padding: 12px; min-width: 220px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <div style="display: flex; align-items: center; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #e8e8e8;">
            <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${this.getStatusColor(status, online)}; margin-right: 8px;"></div>
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #333;">${car.car_name || car.car_ref || '车辆'}</h3>
          </div>
          <div style="margin-bottom: 8px; font-size: 14px;">
            <span style="color: #666;">编号：</span>
            <span style="color: #333; font-weight: 500;">${car.car_ref || '未知'}</span>
          </div>
          <div style="margin-bottom: 8px; font-size: 14px;">
            <span style="color: #666;">状态：</span>
            <span style="color: ${this.getStatusColor(status, online)}; font-weight: 600;">
              ${status}
            </span>
            <span style="color: #999; font-size: 12px; margin-left: 6px;">${online ? '● 在线' : '○ 离线'}</span>
          </div>
          <div style="margin-bottom: 8px; font-size: 14px;">
            <span style="color: #666;">电量：</span>
            <span style="color: ${this.getBatteryColor(car.battery)}; font-weight: 600;">
              ${battery}%
            </span>
          </div>
          <div style="margin-bottom: 8px; font-size: 14px;">
            <span style="color: #666;">区域：</span>
            <span style="color: #333;">${areaName}</span>
          </div>
          <div style="margin-bottom: 8px; font-size: 14px;">
            <span style="color: #666;">区域状态：</span>
            <span style="color: ${isInArea ? '#52c41a' : '#ff4d4f'}; font-weight: 600;">
              ${isInArea ? '在区域内' : '越界'}
            </span>
            ${calculatedInArea !== null && calculatedInArea !== car.in_area ? 
              '<span style="color: #999; font-size: 12px; margin-left: 6px;">(已重新计算)</span>' : ''}
          </div>
          <div style="margin-bottom: 12px; font-size: 13px; color: #666; line-height: 1.5;">
            <div style="margin-top: 4px;">${location}</div>
          </div>
          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e8e8e8; text-align: center;">
            <button data-car-id="${car._id}" 
                    class="car-detail-btn"
                    style="padding: 8px 20px; background: #1890ff; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; transition: background 0.3s;"
                    onmouseover="this.style.background='#40a9ff'"
                    onmouseout="this.style.background='#1890ff'">
              查看详情
            </button>
          </div>
        </div>
      `
    },

    // 获取状态颜色
    getStatusColor(status, online) {
      if (!online) return '#999'
      switch (status) {
        case '空闲': return '#52c41a'
        case '使用中':
        case '前往':
        case '到达':
        case '等待':
        case '开仓':
        case '关仓': return '#1890ff'
        case '故障': return '#ff4d4f'
        case '维护中': return '#faad14'
        default: return '#52c41a'
      }
    },

    // 获取电量颜色
    getBatteryColor(battery) {
      if (battery === null || battery === undefined) return '#999'
      if (battery > 50) return '#52c41a'
      if (battery > 20) return '#faad14'
      return '#ff4d4f'
    },

    // 检查点是否在多边形内（使用射线法）
    pointInPolygon(point, polygon) {
      if (!point || !polygon || polygon.length < 3) return false
      
      const [x, y] = point
      let inside = false
      
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i]
        const [xj, yj] = polygon[j]
        
        const intersect = ((yi > y) !== (yj > y)) && 
          (x < (xj - xi) * (y - yi) / (yj - yi) + xi)
        if (intersect) inside = !inside
      }
      
      return inside
    },

    // 检查车辆是否在区域内（根据实际坐标和区域边界重新计算）
    checkCarInArea(car) {
      // 如果没有坐标或区域边界，使用后端返回的值
      if (!car.location || !car.location.coordinates || !this.areaBoundaries || this.areaBoundaries.length === 0) {
        return car.in_area !== false // 默认认为在区域内
      }

      // 获取车辆坐标
      const [lng, lat] = car.location.coordinates
      if (isNaN(lng) || isNaN(lat)) {
        return car.in_area !== false
      }

      // 查找车辆所属区域
      const vehicleArea = this.areaBoundaries.find(a => 
        Number(a.area_id) === Number(car.area_id)
      )

      if (!vehicleArea) {
        console.warn('未找到车辆所属区域:', car.area_id, '可用区域:', this.areaBoundaries.map(a => a.area_id))
        return car.in_area !== false
      }

      // 获取边界数据（可能已经是处理过的格式）
      let boundary = vehicleArea.boundary
      
      // 如果边界是 GeoJSON 格式，提取坐标
      if (boundary && boundary.coordinates && Array.isArray(boundary.coordinates)) {
        boundary = boundary.coordinates[0] // 取外环
      }
      
      // 确保边界是二维数组格式
      if (!boundary || !Array.isArray(boundary) || boundary.length < 3) {
        console.warn('区域边界数据无效:', vehicleArea.area_name, boundary)
        return car.in_area !== false
      }

      // 确保边界点都是 [lng, lat] 格式
      const normalizedBoundary = boundary.map(point => {
        if (Array.isArray(point) && point.length >= 2) {
          return [Number(point[0]), Number(point[1])]
        }
        return null
      }).filter(p => p !== null)

      if (normalizedBoundary.length < 3) {
        console.warn('区域边界点数量不足:', vehicleArea.area_name, normalizedBoundary.length)
        return car.in_area !== false
      }

      // 检查点是否在多边形内
      const isInArea = this.pointInPolygon([Number(lng), Number(lat)], normalizedBoundary)
      
      console.log('检查车辆是否在区域内:', {
        carName: car.car_name || car.car_ref,
        position: [lng, lat],
        area_id: car.area_id,
        area_name: vehicleArea.area_name,
        backend_in_area: car.in_area,
        calculated_in_area: isInArea,
        boundary_points: normalizedBoundary.length,
        match: isInArea === car.in_area ? '✓' : '✗ 不一致'
      })

      return isInArea
    },

    hexToRgba(hex, alpha = 1) {
      let color = hex.replace('#', '')
      if (color.length === 3) {
        color = color.split('').map(ch => ch + ch).join('')
      }
      const value = parseInt(color, 16)
      const r = (value >> 16) & 255
      const g = (value >> 8) & 255
      const b = value & 255
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },

    // 获取标记层级
    getMarkerZIndex(car) {
      // 确保车辆标记始终在区域边界之上（区域边界zIndex是100）
      // 故障和离线车辆显示在最上层
      if (car.status === '故障' || !car.online) return 200
      if (car.status === '使用中') return 150
      return 120 // 空闲车辆也要在区域边界之上
    },

    // 清除所有标记
    clearMarkers() {
      if (this.map) {
        this.markers.forEach(marker => {
          try {
            this.map.remove(marker)
          } catch (err) {
            console.warn('移除标记失败:', err)
            // 如果 remove 失败，尝试使用 setMap(null)
            try {
              marker.setMap(null)
            } catch (e) {
              console.warn('setMap(null) 也失败:', e)
            }
          }
        })
      }
      this.infoWindows.forEach(iw => {
        try {
          iw.close()
        } catch (err) {
          console.warn('关闭信息窗口失败:', err)
        }
      })
      this.markers = []
      this.markerData = [] // 清空标记数据
      this.markerPositions = [] // 清空坐标数组
      this.infoWindows = []
    },

    // 根据缩放级别计算图标大小
    // 高德地图缩放级别范围通常是 3-18
    // 将缩放级别映射到图标大小范围：5px - 30px
    // 缩放级别 3 对应 5px，缩放级别 18 对应 30px
    calculateIconSizeByZoom(zoom) {
      const minZoom = 3
      const maxZoom = 18
      const minSize = 5
      const maxSize = 30
      
      // 线性映射：将缩放级别映射到图标大小
      // 公式：size = minSize + (zoom - minZoom) * (maxSize - minSize) / (maxZoom - minZoom)
      let iconSize
      if (zoom <= minZoom) {
        iconSize = minSize
      } else if (zoom >= maxZoom) {
        iconSize = maxSize
      } else {
        iconSize = minSize + (zoom - minZoom) * (maxSize - minSize) / (maxZoom - minZoom)
      }
      
      return Math.round(iconSize)
    },

    // 根据地图缩放级别动态调整图标大小（SVG 矢量图优势）
    updateIconSizes() {
      if (!this.map || this.markerData.length === 0) {
        return
      }

      // 清除之前的防抖定时器
      if (this.zoomUpdateTimer) {
        clearTimeout(this.zoomUpdateTimer)
      }

      // 使用较短的防抖时间，让缩放更连贯
      this.zoomUpdateTimer = setTimeout(() => {
        const currentZoom = this.map.getZoom()
        
        // 如果缩放级别没有变化，不更新
        if (currentZoom === this.currentZoom) {
          return
        }

        this.currentZoom = currentZoom

        // 根据缩放级别计算图标大小
        const clampedSize = this.calculateIconSizeByZoom(currentZoom)

        // 更新所有标记的图标
        this.markerData.forEach(({ marker, car, isInArea }) => {
          try {
            const newIcon = this.createCarIcon(car, isInArea, clampedSize)
            marker.setIcon(newIcon)
          } catch (error) {
            console.warn('更新图标失败:', error, car.car_name || car.car_ref)
          }
        })
      }, 50) // 50ms 防抖延迟，让响应更快更连贯
    },

    // 适应所有车辆视野
    fitBounds() {
      if (!this.map || this.markers.length === 0) return

      // 直接使用保存的原始坐标，避免从LngLat对象获取时出错
      const positions = this.markerPositions
        .filter(pos => {
          if (!pos || !Array.isArray(pos) || pos.length < 2) {
            return false
          }
          
          const lng = Number(pos[0])
          const lat = Number(pos[1])
          
          // 验证坐标有效性
          if (isNaN(lng) || isNaN(lat)) {
            console.warn('标记位置无效:', pos)
            return false
          }
          
          // 验证坐标范围
          if (lng < -180 || lng > 180 || lat < -90 || lat > 90) {
            console.warn('标记位置超出范围:', { lng, lat })
            return false
          }
          
          return true
        })
        .map(pos => [Number(pos[0]), Number(pos[1])]) // 确保是数字类型

      if (positions.length === 0) {
        console.warn('没有有效的标记位置，无法适应视野')
        return
      }

      console.log('fitBounds: 使用', positions.length, '个有效位置:', positions)
      console.log('fitBounds: 位置详情:', JSON.parse(JSON.stringify(positions)))

      // 计算边界 - 确保使用纯数字创建LngLat对象
      const bounds = new window.AMap.Bounds()
      positions.forEach((pos, index) => {
        try {
          // 确保坐标是纯数字，不是Vue响应式对象
          const lng = Number(pos[0])
          const lat = Number(pos[1])
          
          console.log(`fitBounds: 处理位置 ${index}:`, { lng, lat, pos, lngType: typeof lng, latType: typeof lat })
          
          if (isNaN(lng) || isNaN(lat)) {
            console.warn(`fitBounds: 位置 ${index} 坐标无效:`, { lng, lat, pos })
            return
          }
          
          // 创建 LngLat 对象
          const lngLat = new window.AMap.LngLat(lng, lat)
          bounds.extend(lngLat)
          console.log(`fitBounds: 位置 ${index} 已添加到边界`)
        } catch (error) {
          console.warn(`fitBounds: 扩展边界失败 (位置 ${index}):`, error, pos)
        }
      })

      // 设置视野 - 使用setFitView方法配合标记，更可靠
      try {
        // 方法1：如果有标记，直接使用setFitView配合标记数组
        if (this.markers.length > 0) {
          this.map.setFitView(this.markers, false, [50, 50, 50, 50])
          console.log('fitBounds: 地图视野已设置成功（使用setFitView配合标记）')
          return
        }
        
        // 方法2：如果没有标记，使用边界计算
        const sw = bounds.getSouthWest()
        const ne = bounds.getNorthEast()
        console.log('fitBounds: 边界范围:', { 
          sw: sw ? { lng: sw.getLng(), lat: sw.getLat() } : null,
          ne: ne ? { lng: ne.getLng(), lat: ne.getLat() } : null
        })
        
        if (sw && ne) {
          // 计算中心点和合适的缩放级别
          const centerLng = (sw.getLng() + ne.getLng()) / 2
          const centerLat = (sw.getLat() + ne.getLat()) / 2
          const center = [centerLng, centerLat]
          
          // 计算合适的缩放级别（根据边界范围）
          const lngDiff = Math.abs(ne.getLng() - sw.getLng())
          const latDiff = Math.abs(ne.getLat() - sw.getLat())
          const maxDiff = Math.max(lngDiff, latDiff)
          
          let zoom = 15
          if (maxDiff > 0.1) zoom = 12
          else if (maxDiff > 0.05) zoom = 13
          else if (maxDiff > 0.01) zoom = 14
          else if (maxDiff > 0.005) zoom = 15
          else zoom = 16
          
          this.map.setCenter(center)
          this.map.setZoom(zoom)
          console.log('fitBounds: 地图视野已设置成功（使用中心点和缩放级别）', { center, zoom })
        } else {
          console.warn('fitBounds: 边界无效，使用第一个位置作为中心点')
          if (positions.length > 0) {
            this.map.setCenter(positions[0])
            this.map.setZoom(15)
            console.log('fitBounds: 使用备用方案，设置中心点和缩放级别')
          }
        }
      } catch (error) {
        console.error('设置地图视野失败:', error)
        // 备用方案：直接设置中心点和缩放级别
        try {
          if (positions.length > 0) {
            this.map.setCenter(positions[0])
            this.map.setZoom(15)
            console.log('fitBounds: 使用备用方案，设置中心点和缩放级别')
          }
        } catch (fallbackError) {
          console.error('备用方案也失败:', fallbackError)
        }
      }
    },

    // 回到中心
    centerMap() {
      if (!this.map) return
      this.map.setCenter(this.center)
      this.map.setZoom(this.zoom)
    },

    // 刷新标记
    refreshMarkers() {
      this.$emit('refresh')
    },

    // 更新区域边界
    updateAreaBoundaries(boundaries) {
      console.log('CarMap 更新边界: mapLoaded=', this.mapLoaded, 'hasMap=', !!this.map, '数量=', boundaries ? boundaries.length : 0, boundaries)
      if (!this.map || !boundaries || boundaries.length === 0) return

      // 清除旧边界
      this.clearAreaBoundaries()
      // 重置适应标记，允许重新适应
      this.hasFittedToArea = false

      boundaries.forEach(area => {
        // 只显示选中区域
        const isSelected = this.selectedAreaId === area.area_id
        if (!isSelected) {
          return // 跳过非选中区域
        }

        const boundaryPoints = this.normalizeBoundary(area.boundary)
        console.log('处理区域边界:', area, 'boundaryPoints:', boundaryPoints)
        if (!boundaryPoints || boundaryPoints.length === 0) {
          console.warn('区域边界数据无效:', area)
          return
        }

        // 设置样式 - 选中区域使用蓝色
        const strokeColor = '#1890ff'
        const fillColor = 'rgba(24, 144, 255, 0.1)'
        const strokeWeight = 3

        // 创建路径（排除闭合点）
        const path = boundaryPoints.map((coord, index) => {
          const lng = Number(coord[0])
          const lat = Number(coord[1])
          if (isNaN(lng) || isNaN(lat)) {
            console.warn(`区域 ${area.area_id} 第 ${index} 个点坐标无效:`, coord)
          }
          return new window.AMap.LngLat(lng, lat)
        })
        console.log('区域路径坐标:', path)

        // 创建多边形
        // 降低区域边界的zIndex，确保车辆标记显示在上层
        const polygon = new window.AMap.Polygon({
          path: path,
          strokeColor: strokeColor,
          strokeWeight: strokeWeight,
          fillColor: fillColor,
          fillOpacity: 0.2, // 降低填充透明度，避免过度遮挡标记
          strokeStyle: 'solid',
          strokeOpacity: 0.8,
          zIndex: isSelected ? 50 : 10 // 降低zIndex，确保车辆标记（120-200）在上层
        })

        // 创建信息窗口内容
        const infoWindow = new window.AMap.InfoWindow({
          content: this.createAreaInfoWindowContent(area),
          offset: new window.AMap.Pixel(0, -10),
          closeWhenClickMap: true
        })

        // 点击多边形显示信息窗口
        polygon.on('click', (e) => {
          // 关闭其他信息窗口
          this.areaInfoWindows.forEach(iw => iw.close())
          infoWindow.open(this.map, e.lnglat)
          
          // 使用事件委托，监听信息窗口容器的点击事件
          const bindButtonClick = () => {
            // 查找按钮（使用区域ID作为标识）
            const selectBtn = document.querySelector('.area-select-btn[data-area-id="' + area.area_id + '"]')
            if (selectBtn) {
              // 移除可能存在的旧事件监听器
              const newBtn = selectBtn.cloneNode(true)
              selectBtn.parentNode.replaceChild(newBtn, selectBtn)
              
              // 绑定点击事件
              newBtn.addEventListener('click', (event) => {
                event.preventDefault()
                event.stopPropagation()
                console.log('点击选择区域按钮:', area)
                // 触发区域选择事件
                this.$emit('area-select', area)
                // 关闭信息窗口
                infoWindow.close()
              })
            } else {
              // 如果按钮还没渲染出来，稍后再试
              setTimeout(bindButtonClick, 50)
            }
          }
          
          // 等待信息窗口 DOM 渲染
          setTimeout(bindButtonClick, 100)
        })

        // 鼠标悬停效果
        polygon.on('mouseover', () => {
          if (!isSelected) {
            polygon.setOptions({
              strokeWeight: 3,
              fillOpacity: 0.4
            })
          }
        })

        polygon.on('mouseout', () => {
          if (!isSelected) {
            polygon.setOptions({
              strokeWeight: 2,
              fillOpacity: 0.3
            })
          }
        })

        // 添加到地图
        polygon.setMap(this.map)
        this.areaPolygons.push({
          polygon: polygon,
          areaId: area.area_id,
          infoWindow: infoWindow
        })
        this.areaInfoWindows.push(infoWindow)

        // 添加中心点标记
        if (area.center && Array.isArray(area.center) && area.center.length === 2) {
          this.addCenterMarker(area.center, area.area_name)
        }
        
        // 自动适应区域边界（参考区域边界编辑的逻辑）
        // 只在第一次加载选中区域时适应，避免重复触发
        if (isSelected && polygon && !this.hasFittedToArea) {
          // 使用 setFitView 自动适应区域边界
          this.$nextTick(() => {
            try {
              this.map.setFitView([polygon], false, [50, 50, 50, 50])
              this.hasFittedToArea = true // 标记已适应
              console.log('地图已自动适应区域边界')
            } catch (error) {
              console.warn('适应区域边界失败:', error)
            }
          })
        }
      })
    },

    // 添加区域中心点标记
    addCenterMarker(centerCoords, areaName) {
      // 清除旧的中心点标记
      if (this.centerMarker) {
        this.centerMarker.setMap(null)
        this.centerMarker = null
      }

      const lng = Number(centerCoords[0])
      const lat = Number(centerCoords[1])
      
      if (isNaN(lng) || isNaN(lat)) {
        console.warn('中心点坐标无效:', centerCoords)
        return
      }

      // 创建自定义图标（使用 Canvas 绘制定位图标）
      const canvas = document.createElement('canvas')
      canvas.width = 32
      canvas.height = 32
      const ctx = canvas.getContext('2d')
      
      // 绘制外圈
      ctx.beginPath()
      ctx.arc(16, 16, 12, 0, Math.PI * 2)
      ctx.fillStyle = '#1890ff'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.stroke()
      
      // 绘制内圈
      ctx.beginPath()
      ctx.arc(16, 16, 5, 0, Math.PI * 2)
      ctx.fillStyle = '#fff'
      ctx.fill()
      
      // 绘制四个方向的指示线
      ctx.strokeStyle = '#1890ff'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      // 上
      ctx.beginPath()
      ctx.moveTo(16, 4)
      ctx.lineTo(16, 8)
      ctx.stroke()
      // 下
      ctx.beginPath()
      ctx.moveTo(16, 24)
      ctx.lineTo(16, 28)
      ctx.stroke()
      // 左
      ctx.beginPath()
      ctx.moveTo(4, 16)
      ctx.lineTo(8, 16)
      ctx.stroke()
      // 右
      ctx.beginPath()
      ctx.moveTo(24, 16)
      ctx.lineTo(28, 16)
      ctx.stroke()

      const icon = new window.AMap.Icon({
        size: new window.AMap.Size(32, 32),
        image: canvas.toDataURL(),
        imageOffset: new window.AMap.Pixel(0, 0),
        imageSize: new window.AMap.Size(32, 32)
      })

      // 创建标记
      this.centerMarker = new window.AMap.Marker({
        position: [lng, lat],
        icon: icon,
        title: areaName ? areaName + ' - 中心点' : '区域中心点',
        zIndex: 200, // 确保在区域边界之上
        offset: new window.AMap.Pixel(-16, -16) // 图标中心对齐
      })

      // 添加信息窗口
      const infoWindow = new window.AMap.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 150px;">
            <div style="font-weight: bold; margin-bottom: 4px; color: #1890ff;">
              ${areaName || '区域中心点'}
            </div>
            <div style="font-size: 12px; color: #666;">
              坐标：${lng.toFixed(6)}, ${lat.toFixed(6)}
            </div>
          </div>
        `,
        offset: new window.AMap.Pixel(0, -10),
        closeWhenClickMap: true
      })

      // 点击标记显示信息
      this.centerMarker.on('click', () => {
        infoWindow.open(this.map, [lng, lat])
      })

      // 添加到地图
      this.centerMarker.setMap(this.map)
    },

    // 规范化边界数据格式
    normalizeBoundary(boundary) {
      if (!boundary) return []
      if (Array.isArray(boundary)) return boundary

      if (boundary.coordinates) {
        if (Array.isArray(boundary.coordinates[0])) {
          return boundary.coordinates[0]
        }
        if (Array.isArray(boundary.coordinates)) {
          return boundary.coordinates
        }
      }

      if (Array.isArray(boundary.points)) {
        return boundary.points
      }

      return []
    },

    // 创建区域信息窗口内容
    createAreaInfoWindowContent(area) {
      return `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 10px 0; font-size: 16px; color: #1890ff;">
            ${area.area_name || '未知区域'}
          </h3>
          <div style="margin-bottom: 8px;">
            <strong>区域ID：</strong>${area.area_id || '未知'}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>中心点：</strong>
            ${area.center && area.center.length === 2 
              ? `${area.center[0].toFixed(6)}, ${area.center[1].toFixed(6)}` 
              : '未知'}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>边界点数：</strong>
            ${area.boundary ? area.boundary.length : 0}
          </div>
          <div style="margin-top: 10px; text-align: center;">
            <button 
              data-area-id="${area.area_id}" 
              class="area-select-btn"
              style="padding: 5px 15px; background: #1890ff; color: #fff; border: none; border-radius: 4px; cursor: pointer;">
              选择此区域
            </button>
          </div>
        </div>
      `
    },

    // 高亮指定区域（使用蓝色）
    highlightArea(areaId) {
      this.areaPolygons.forEach(item => {
        const isSelected = item.areaId === areaId
        const polygon = item.polygon

        if (isSelected) {
          polygon.setOptions({
            strokeColor: '#1890ff',
            strokeWeight: 3,
            fillColor: 'rgba(24, 144, 255, 0.1)',
            fillOpacity: 0.4,
            zIndex: 100
          })
        }
      })
    },

    // 清除区域边界
    clearAreaBoundaries() {
      this.areaPolygons.forEach(item => {
        item.polygon.setMap(null)
        item.polygon = null
        if (item.infoWindow) {
          item.infoWindow.close()
          item.infoWindow = null
        }
      })
      this.areaInfoWindows.forEach(iw => {
        iw.close()
        iw = null
      })
      this.areaPolygons = []
      this.areaInfoWindows = []
      
      // 清除中心点标记
      if (this.centerMarker) {
        this.centerMarker.setMap(null)
        this.centerMarker = null
      }
    },

    // 切换区域边界显示
    toggleAreaBoundaries() {
      this.showAreaBoundaries = !this.showAreaBoundaries
    },
    
    // 适应选中区域边界
    fitToSelectedArea() {
      if (!this.map || !this.selectedAreaId) return
      
      // 查找选中区域的多边形
      const selectedPolygon = this.areaPolygons.find(item => item.areaId === this.selectedAreaId)
      if (selectedPolygon && selectedPolygon.polygon) {
        try {
          this.map.setFitView([selectedPolygon.polygon], false, [50, 50, 50, 50])
          this.hasFittedToArea = true
          console.log('地图已适应选中区域边界')
        } catch (error) {
          console.warn('适应区域边界失败:', error)
        }
      }
    }
  },
  
  beforeDestroy() {
    // 清除缩放更新定时器
    if (this.zoomUpdateTimer) {
      clearTimeout(this.zoomUpdateTimer)
      this.zoomUpdateTimer = null
    }
    this.clearMarkers()
    this.clearAreaBoundaries()
    if (this.map) {
      this.map.destroy()
    }
  }
}
</script>

<style lang="less" scoped>
.car-map-container {
  position: relative;
  width: 100%;
  height: 600px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;

  .map-container {
    width: 100%;
    height: 100%;
  }

  .map-legend {
    position: absolute;
    top: 10px;
    left: 10px;
    background: rgba(255, 255, 255, 0.95);
    padding: 12px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-width: 200px;
    max-height: 80vh;
    overflow-y: auto;

    .legend-section {
      margin-bottom: 12px;
      
      &:last-child {
        margin-bottom: 0;
      }

      .legend-title {
        font-weight: 600;
        font-size: 13px;
        margin-bottom: 8px;
        color: #333;
        border-bottom: 1px solid #e8e8e8;
        padding-bottom: 4px;
      }
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      margin-bottom: 6px;
      
      &:last-child {
        margin-bottom: 0;
      }

      .legend-icon {
        width: 16px;
        height: 16px;
        border-radius: 2px;
        display: inline-block;
        flex-shrink: 0;
      }
    }
  }

  .map-controls {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .car-status-label {
    color: #fff;
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 999px;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.6);
  }
}
</style>

